import React, { useState, useEffect, useCallback, useMemo } from 'react';
import DashboardLayout from '../../components/layout/DashboardLayout';
import { TabBar, TabBtn } from '../../components/common/Tabs';
import { dispatchAPI, salesPipelineAPI, operationsAPI, uploadAPI } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import {
  HiOutlineCube, HiOutlineCheckCircle, HiOutlineRefresh,
  HiOutlinePhotograph, HiOutlineTruck, HiOutlineSearch,
  HiOutlineClipboardList, HiOutlineX, HiOutlineArrowRight,
  HiOutlineArrowLeft, HiOutlineSave, HiOutlinePlus,
  HiOutlinePencil, HiOutlineTrash, HiOutlineDocumentText
} from 'react-icons/hi';
import { io } from 'socket.io-client';
import toast from 'react-hot-toast';
import OrderReleaseInfo from '../../components/common/OrderReleaseInfo';
import OrderWorkflowBanner, { OrderStepBadge, determineOrderStep, WORKFLOW_9_STEPS } from '../../components/common/OrderWorkflowBanner';
import {
  DispatchQcModal, QcStatusBadge, QcActionButton, canRunDispatchQc
} from '../../components/common/DispatchQualityCheck';

const SOCKET_URL = process.env.REACT_APP_API_URL || (window.location.port ? `${window.location.protocol}//${window.location.hostname}:9898` : window.location.origin);

const getMediaFullUrl = (url) => {
  if (!url) return '';
  if (url.startsWith('http://') || url.startsWith('https://') || url.startsWith('data:')) return url;
  const base = process.env.REACT_APP_API_URL || (window.location.port ? `${window.location.protocol}//${window.location.hostname}:9898` : window.location.origin);
  const cleanBase = base.replace(/\/api\/?$/, '');
  const cleanUrl = url.startsWith('/') ? url : `/${url}`;
  return `${cleanBase}${cleanUrl}`;
};

const emptyPipelineForm = {
  customerName: '',
  customerContact: '',
  customerEmail: '',
  shippingAddress: '',
  customerGST: '',
  stage: 'sales_order', // Default to sales_order so it directly becomes an active Assembly job
  dragChainConfig: {
    modelNumber: '',
    series: '',
    width: '',
    radius: '',
    pitch: 46,
    numberOfLinks: 22,
    mountingBrackets: 'Standard',
    quantity: 1,
    length: '1000 MM',
    ratePerMeter: 1200
  },
  grandTotal: 1200,
  remarks: ''
};

export default function AssemblyDashboard() {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('assemble'); // 'assemble', 'packing', 'dispatched', 'pipeline_overview'
  const [dispatches, setDispatches] = useState([]);
  const [pipelineOrders, setPipelineOrders] = useState([]);
  const [productTemplates, setProductTemplates] = useState([]);
  const [stats, setStats] = useState({ assemble: 0, hold: 0, packing: 0, dispatched: 0 });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [pipelineStageFilter, setPipelineStageFilter] = useState('all');
  const [uploadingMedia, setUploadingMedia] = useState(false);
  const [selectedWorkflowStep, setSelectedWorkflowStep] = useState(7); // Default step 7 Assembly

  // Process Assembly / Packing Modal
  const [showModal, setShowModal] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [modalStep, setModalStep] = useState(1); // 1: Assembly, 2: Packing, 3: Billing, 4: Logistics/Dispatch
  const [savingDraft, setSavingDraft] = useState(false);

  // Pre-Dispatch Quality Check Modal (meters only) - see components/common/DispatchQualityCheck
  const [showQcModal, setShowQcModal] = useState(false);
  const [qcOrder, setQcOrder] = useState(null);

  // New Order / Pipeline Deal Creation Modal
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingPipelineId, setEditingPipelineId] = useState(null);
  const [pipelineForm, setPipelineForm] = useState(emptyPipelineForm);
  const [creatingOrder, setCreatingOrder] = useState(false);

  // Form State for Assembly / Packing Process
  const [formData, setFormData] = useState({
    status: 'assemble',
    salesOrderRef: '',
    customerName: '',
    customerContact: '',
    customerEmail: '',
    customerAddress: '',
    assemblyNotes: '',
    assemblyMediaUrl: '',
    assemblyVideoUrl: '',
    linksAssembled: '',
    bracketsFitted: 'yes',
    totalBoxes: 1,
    totalWeight: '',
    packingNotes: '',
    packingMediaUrl: '',
    invoiceNo: '',
    billingInvoiceUrl: '',
    courier: '',
    lrNumber: '',
    trackingNumber: '',
    dispatchMediaUrl: '',
    dispatchNotes: ''
  });

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [dispRes, statsRes, pipeRes, tmplRes] = await Promise.all([
        dispatchAPI.getAll({ limit: 100, search }),
        dispatchAPI.getStats().catch(() => ({ data: { data: {} } })),
        salesPipelineAPI.getAll({ limit: 100, search }).catch(() => ({ data: { data: [] } })),
        operationsAPI.getProductTemplates().catch(() => ({ data: { data: [] } }))
      ]);

      setDispatches(dispRes.data.data || []);
      setStats(statsRes.data.data || {});
      // Sales Orders appear in the store only after Admin approval
      setPipelineOrders((pipeRes.data.data || []).filter(o => o.stage !== 'sales_order' || o.approvalStatus === 'approved'));
      setProductTemplates(tmplRes.data.data || []);
    } catch (err) {
      console.error('Failed to load Assembly data:', err);
      toast.error('Failed to load Assembly orders');
    } finally {
      setLoading(false);
    }
  }, [search]);

  useEffect(() => {
    fetchData();

    let socket;
    if (user?.organizationId) {
      socket = io(SOCKET_URL, { withCredentials: true });
      socket.emit('join_org', user.organizationId);

      socket.on('dispatch_updated', fetchData);
      socket.on('sales_updated', fetchData);
      socket.on('build_updated', fetchData);
    }

    return () => {
      if (socket) socket.disconnect();
    };
  }, [fetchData, user?.organizationId]);

  // Open Process Modal
  const handleOpenProcessModal = (order, targetModalStep = 1) => {
    setSelectedOrder(order);
    setModalStep(targetModalStep);
    setFormData({
      status: order.status || 'assemble',
      salesOrderRef: order.salesOrderRef || order.salesOrderNumber || '',
      customerName: order.customerName || '',
      customerContact: order.customerContact || '',
      customerEmail: order.customerEmail || '',
      customerAddress: order.customerAddress || order.shippingAddress || '',
      assemblyNotes: order.assemblyNotes || '',
      assemblyMediaUrl: order.assemblyMediaUrl || '',
      assemblyVideoUrl: order.assemblyVideoUrl || '',
      linksAssembled: order.linksAssembled || (order.items?.[0]?.dragChainConfig?.numberOfLinks) || (order.dragChainConfig?.numberOfLinks) || '',
      bracketsFitted: order.bracketsFitted || 'yes',
      totalBoxes: order.totalBoxes || 1,
      totalWeight: order.totalWeight || '',
      packingNotes: order.packingNotes || '',
      packingMediaUrl: order.packingMediaUrl || '',
      invoiceNo: order.invoiceNo || order.invoiceNumber || '',
      billingInvoiceUrl: order.billingInvoiceUrl || '',
      courier: order.courier || '',
      lrNumber: order.lrNumber || '',
      trackingNumber: order.trackingNumber || '',
      dispatchMediaUrl: order.dispatchMediaUrl || '',
      dispatchNotes: order.dispatchNotes || ''
    });
    setShowModal(true);
  };

  // Can the logged-in user record a quality check?
  const canRunQc = canRunDispatchQc(user);

  // Open the shared pre-dispatch QC modal for an order
  const handleOpenQcModal = (order) => {
    setQcOrder(order);
    setShowQcModal(true);
  };

  const handleCloseQcModal = () => {
    setShowQcModal(false);
    setQcOrder(null);
  };

  const handleMediaUpload = async (file, fieldKey) => {
    if (!file) return;
    try {
      setUploadingMedia(true);
      const fd = new FormData();
      fd.append('file', file);
      const res = await uploadAPI.upload(fd);
      if (res.data?.url || res.data?.fileUrl) {
        const uploadedPath = res.data.url || res.data.fileUrl;
        setFormData(prev => ({ ...prev, [fieldKey]: uploadedPath }));
        toast.success('📷 Photo/Video uploaded successfully!');
      }
    } catch (err) {
      toast.error('Upload failed. Please try again.');
    } finally {
      setUploadingMedia(false);
    }
  };

  const handleSaveDraft = async () => {
    if (!selectedOrder) return;
    try {
      setSavingDraft(true);
      if (selectedOrder._id) {
        await dispatchAPI.update(selectedOrder._id, {
          ...formData,
          isDraft: true
        });
      }
      toast.success('💾 Step Progress Saved as Draft!');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to save draft');
    } finally {
      setSavingDraft(false);
    }
  };

  const handleAdvanceStep = async (nextStatus) => {
    if (!selectedOrder) return;
    try {
      setSavingDraft(true);
      const payload = {
        ...formData,
        status: nextStatus || formData.status
      };
      await dispatchAPI.update(selectedOrder._id, payload);
      toast.success(`✅ Order updated to ${nextStatus.toUpperCase()}!`);
      setShowModal(false);
      fetchData();
    } catch (err) {
      // Mandatory pre-dispatch quality check blocked this transition
      if (err.response?.data?.qcBlocked) {
        toast.error(err.response.data.message, { duration: 6000 });
        setShowModal(false);
        handleOpenQcModal(selectedOrder);
        return;
      }
      toast.error(err.response?.data?.message || 'Failed to update order');
    } finally {
      setSavingDraft(false);
    }
  };

  // Quick 1-Click Pipeline Stage Advancement
  const handleAdvancePipelineStage = async (order, targetStage) => {
    try {
      await salesPipelineAPI.update(order._id, {
        stage: targetStage,
        auditNote: `Stage advanced to ${targetStage.toUpperCase()} from Assembly Workspace`
      });
      toast.success(`✅ Order moved to Step ${targetStage.toUpperCase()}!`);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to advance stage');
    }
  };

  // Delete Pipeline Order
  const handleDeletePipelineOrder = async (id) => {
    if (!window.confirm('Are you sure you want to delete this order?')) return;
    try {
      await salesPipelineAPI.delete(id);
      toast.success('Order deleted successfully');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to delete order');
    }
  };

  // Handle Create / Edit Pipeline Order
  const handleOpenCreateModal = (orderToEdit = null) => {
    if (orderToEdit) {
      setEditingPipelineId(orderToEdit._id);
      const cfg = orderToEdit.items?.[0]?.dragChainConfig || orderToEdit.dragChainConfig || {};
      setPipelineForm({
        customerName: orderToEdit.customerName || '',
        customerContact: orderToEdit.customerContact || '',
        customerEmail: orderToEdit.customerEmail || '',
        shippingAddress: orderToEdit.shippingAddress || orderToEdit.customerAddress || '',
        customerGST: orderToEdit.customerGST || '',
        stage: orderToEdit.stage || 'sales_order',
        dragChainConfig: {
          modelNumber: cfg.modelNumber || orderToEdit.modelNumber || '',
          series: cfg.series || '',
          width: cfg.width || '',
          radius: cfg.radius || '',
          pitch: cfg.pitch || 46,
          numberOfLinks: cfg.numberOfLinks || 22,
          mountingBrackets: cfg.mountingBrackets || 'Standard',
          quantity: cfg.quantity || orderToEdit.quantity || 1,
          length: cfg.length || '1000 MM',
          ratePerMeter: cfg.ratePerMeter || orderToEdit.unitPrice || 1200
        },
        grandTotal: orderToEdit.grandTotal || orderToEdit.totalAmount || 1200,
        remarks: orderToEdit.remarks || ''
      });
    } else {
      setEditingPipelineId(null);
      setPipelineForm(emptyPipelineForm);
    }
    setShowCreateModal(true);
  };

  const handleSavePipelineOrder = async (e) => {
    e.preventDefault();
    if (!pipelineForm.customerName.trim()) {
      toast.error('Please provide Customer Name');
      return;
    }

    try {
      setCreatingOrder(true);
      const payload = {
        ...pipelineForm,
        items: [{
          modelNumber: pipelineForm.dragChainConfig.modelNumber || 'Standard Drag Chain',
          quantity: pipelineForm.dragChainConfig.quantity || 1,
          unit: 'meter',
          unitPrice: pipelineForm.dragChainConfig.ratePerMeter || 1200,
          totalPrice: pipelineForm.grandTotal,
          dragChainConfig: pipelineForm.dragChainConfig
        }]
      };

      if (editingPipelineId) {
        await salesPipelineAPI.update(editingPipelineId, payload);
        toast.success('✅ Pipeline Order Updated Successfully!');
      } else {
        await salesPipelineAPI.create(payload);
        toast.success('✅ New Pipeline & Assembly Order Created Successfully!');
      }

      setShowCreateModal(false);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to save order');
    } finally {
      setCreatingOrder(false);
    }
  };

  // Filtered lists for each tab
  const assembleOrders = useMemo(() => {
    return dispatches.filter(d => d.status === 'assemble' || (!d.status && !d.packedAt && !d.dispatchedAt));
  }, [dispatches]);

  const packingOrders = useMemo(() => {
    return dispatches.filter(d => d.status === 'packing');
  }, [dispatches]);

  const dispatchedOrders = useMemo(() => {
    return dispatches.filter(d => d.status === 'dispatched');
  }, [dispatches]);

  const filteredPipelineOrders = useMemo(() => {
    if (pipelineStageFilter === 'all') return pipelineOrders;
    return pipelineOrders.filter(order => {
      const step = determineOrderStep(order);
      if (pipelineStageFilter === 'lead') return step === 1;
      if (pipelineStageFilter === 'quotation') return step === 2;
      if (pipelineStageFilter === 'customer_po') return step === 3;
      if (pipelineStageFilter === 'pi') return step === 4;
      if (pipelineStageFilter === 'invoice') return step === 5;
      if (pipelineStageFilter === 'payment') return step === 6;
      if (pipelineStageFilter === 'assemble') return step === 7;
      if (pipelineStageFilter === 'packing') return step === 8;
      if (pipelineStageFilter === 'dispatched') return step === 9;
      return true;
    });
  }, [pipelineOrders, pipelineStageFilter]);

  return (
    <DashboardLayout>
      <div style={{ maxWidth: '1400px', margin: '0 auto', paddingBottom: '40px' }}>
        
        {/* Top Header */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: '16px',
          marginBottom: '20px'
        }}>
          <div>
            <h1 style={{
              margin: 0,
              fontSize: '1.6rem',
              fontWeight: 800,
              color: 'var(--text-primary)',
              display: 'flex',
              alignItems: 'center',
              gap: '10px'
            }}>
              <span style={{
                background: 'linear-gradient(135deg, #f59e0b, #d97706)',
                color: '#fff',
                padding: '6px 12px',
                borderRadius: '10px',
                fontSize: '1.1rem'
              }}>
                🔧
              </span>
              Assembly & Packaging Line
            </h1>
            <p style={{ margin: '4px 0 0', color: 'var(--text-dim)', fontSize: '0.86rem' }}>
              Step 7: Physical Assembly & QC Proofs ➔ Step 8: Box Packaging & Weights ➔ Step 9: Logistics Dispatch
            </p>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
            <div className="form-input-icon" style={{ width: '240px' }}>
              <HiOutlineSearch className="icon" />
              <input
                type="text"
                className="form-input"
                style={{ padding: '8px 12px 8px 36px', fontSize: '0.85rem' }}
                placeholder="Search by SO#, customer, model..."
                value={search}
                onChange={e => setSearch(e.target.value)}
              />
            </div>

            {/* ➕ PROMINENT ADD WORK ORDER BUTTON */}
            <button
              className="btn btn-primary btn-sm"
              onClick={() => handleOpenCreateModal(null)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                fontWeight: 700,
                background: 'linear-gradient(135deg, #f59e0b, #d97706)',
                border: 'none',
                boxShadow: '0 4px 12px rgba(245,158,11,0.3)'
              }}
            >
              <HiOutlinePlus style={{ strokeWidth: 3 }} /> + New Order / Job
            </button>

            <button className="btn btn-secondary btn-sm" onClick={fetchData} title="Reload Data">
              <HiOutlineRefresh /> Reload
            </button>
          </div>
        </div>

        {/* 🔄 End-to-End 9-Step Lifecycle Banner */}
        <OrderWorkflowBanner
          activeStep={selectedWorkflowStep}
          onStepClick={(stepObj) => {
            setSelectedWorkflowStep(stepObj.step);
            if (stepObj.step === 7) setActiveTab('assemble');
            else if (stepObj.step === 8) setActiveTab('packing');
            else if (stepObj.step === 9) setActiveTab('dispatched');
            else {
              setActiveTab('pipeline_overview');
              setPipelineStageFilter(stepObj.key);
            }
          }}
        />

        {/* Metric Overview Cards */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
          gap: '16px',
          marginBottom: '24px'
        }}>
          <div
            onClick={() => { setActiveTab('assemble'); setSelectedWorkflowStep(7); }}
            style={{
              padding: '18px 20px',
              borderRadius: '14px',
              background: activeTab === 'assemble' ? 'rgba(245, 158, 11, 0.12)' : 'var(--bg-secondary)',
              border: `1px solid ${activeTab === 'assemble' ? '#f59e0b' : 'var(--border)'}`,
              cursor: 'pointer',
              transition: 'all 0.2s ease'
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontSize: '0.82rem', fontWeight: 600, color: '#f59e0b' }}>Step 7: In Assembly</span>
              <span style={{ fontSize: '1.2rem' }}>🔧</span>
            </div>
            <div style={{ fontSize: '1.8rem', fontWeight: 800, color: 'var(--text-primary)', marginTop: '8px' }}>
              {stats.assemble || assembleOrders.length || 0}
            </div>
            <div style={{ fontSize: '0.74rem', color: 'var(--text-dim)', marginTop: '4px' }}>
              Orders ready for physical assembly
            </div>
          </div>

          <div
            onClick={() => { setActiveTab('packing'); setSelectedWorkflowStep(8); }}
            style={{
              padding: '18px 20px',
              borderRadius: '14px',
              background: activeTab === 'packing' ? 'rgba(99, 102, 241, 0.12)' : 'var(--bg-secondary)',
              border: `1px solid ${activeTab === 'packing' ? '#6366f1' : 'var(--border)'}`,
              cursor: 'pointer',
              transition: 'all 0.2s ease'
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontSize: '0.82rem', fontWeight: 600, color: '#6366f1' }}>Step 8: Box Packaging</span>
              <span style={{ fontSize: '1.2rem' }}>📦</span>
            </div>
            <div style={{ fontSize: '1.8rem', fontWeight: 800, color: 'var(--text-primary)', marginTop: '8px' }}>
              {stats.packing || packingOrders.length || 0}
            </div>
            <div style={{ fontSize: '0.74rem', color: 'var(--text-dim)', marginTop: '4px' }}>
              Assembled items waiting for cartons
            </div>
          </div>

          <div
            onClick={() => { setActiveTab('dispatched'); setSelectedWorkflowStep(9); }}
            style={{
              padding: '18px 20px',
              borderRadius: '14px',
              background: activeTab === 'dispatched' ? 'rgba(34, 197, 94, 0.12)' : 'var(--bg-secondary)',
              border: `1px solid ${activeTab === 'dispatched' ? '#22c55e' : 'var(--border)'}`,
              cursor: 'pointer',
              transition: 'all 0.2s ease'
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontSize: '0.82rem', fontWeight: 600, color: '#22c55e' }}>Step 9: Dispatched</span>
              <span style={{ fontSize: '1.2rem' }}>🚚</span>
            </div>
            <div style={{ fontSize: '1.8rem', fontWeight: 800, color: 'var(--text-primary)', marginTop: '8px' }}>
              {stats.dispatched || dispatchedOrders.length || 0}
            </div>
            <div style={{ fontSize: '0.74rem', color: 'var(--text-dim)', marginTop: '4px' }}>
              Outward shipped & completed
            </div>
          </div>

          <div
            onClick={() => { setActiveTab('pipeline_overview'); }}
            style={{
              padding: '18px 20px',
              borderRadius: '14px',
              background: activeTab === 'pipeline_overview' ? 'rgba(14, 165, 233, 0.12)' : 'var(--bg-secondary)',
              border: `1px solid ${activeTab === 'pipeline_overview' ? '#0ea5e9' : 'var(--border)'}`,
              cursor: 'pointer',
              transition: 'all 0.2s ease'
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontSize: '0.82rem', fontWeight: 600, color: '#0ea5e9' }}>Total Pipeline</span>
              <span style={{ fontSize: '1.2rem' }}>🔄</span>
            </div>
            <div style={{ fontSize: '1.8rem', fontWeight: 800, color: 'var(--text-primary)', marginTop: '8px' }}>
              {pipelineOrders.length || dispatches.length || 0}
            </div>
            <div style={{ fontSize: '0.74rem', color: 'var(--text-dim)', marginTop: '4px' }}>
              9-Step End-to-End order tracking
            </div>
          </div>
        </div>

        {/* Tab Navigation Menu */}
        <div className="tab-row">
          <TabBar>
            <TabBtn active={activeTab === 'assemble'} onClick={() => { setActiveTab('assemble'); setSelectedWorkflowStep(7); }}>
              🔧 1. Assembly Queue ({assembleOrders.length})
            </TabBtn>
            <TabBtn active={activeTab === 'packing'} onClick={() => { setActiveTab('packing'); setSelectedWorkflowStep(8); }}>
              📦 2. Box Packing Queue ({packingOrders.length})
            </TabBtn>
            <TabBtn active={activeTab === 'dispatched'} onClick={() => { setActiveTab('dispatched'); setSelectedWorkflowStep(9); }}>
              🚚 3. Completed & Dispatched ({dispatchedOrders.length})
            </TabBtn>
            <TabBtn active={activeTab === 'pipeline_overview'} onClick={() => { setActiveTab('pipeline_overview'); }}>
              🔄 4. Full 9-Step Pipeline Tracker ({pipelineOrders.length})
            </TabBtn>
          </TabBar>

          <div className="tab-row-actions">
            <button
              className="btn btn-sm"
              onClick={() => handleOpenCreateModal(null)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                fontWeight: 700,
                background: 'rgba(245,158,11,0.15)',
                color: '#f59e0b',
                border: '1px solid #f59e0b'
              }}
            >
              <HiOutlinePlus /> + Add Work Order
            </button>
          </div>
        </div>

        {/* TAB 1: ASSEMBLY QUEUE (STEP 7) */}
        {activeTab === 'assemble' && (
          <div className="azure-card" style={{ padding: '24px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', flexWrap: 'wrap', gap: '10px' }}>
              <div>
                <h3 style={{ margin: 0, fontSize: '1.1rem', color: 'var(--text-primary)', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  🔧 Step 7: Drag Chain Assembly Work Orders
                  <span className="azure-badge warning">{assembleOrders.length} Pending</span>
                </h3>
                <p style={{ margin: '4px 0 0', color: 'var(--text-dim)', fontSize: '0.8rem' }}>
                  Verify chain series, calculate links, fit end brackets, take assembly photo/video proof, and advance to Box Packing.
                </p>
              </div>

              <button
                className="btn btn-primary btn-sm"
                onClick={() => handleOpenCreateModal(null)}
                style={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: '6px' }}
              >
                <HiOutlinePlus /> + New Assembly Job
              </button>
            </div>

            {loading ? (
              <div style={{ textAlign: 'center', padding: '40px' }}><div className="spinner" /></div>
            ) : assembleOrders.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '50px 20px', color: 'var(--text-dim)' }}>
                <div style={{ fontSize: '2.5rem', marginBottom: '8px' }}>✅</div>
                <div style={{ fontWeight: 700, fontSize: '1.1rem', color: 'var(--text-primary)' }}>Assembly Queue Clear!</div>
                <div style={{ fontSize: '0.85rem', marginBottom: '16px' }}>No pending orders requiring physical drag chain assembly right now.</div>
                <button className="btn btn-primary btn-sm" onClick={() => handleOpenCreateModal(null)}>
                  <HiOutlinePlus /> Create First Assembly Order
                </button>
              </div>
            ) : (
              <div className="azure-table-container">
                <table className="azure-table">
                  <thead>
                    <tr>
                      <th>SO Reference & Customer</th>
                      <th>Step Status</th>
                      <th>Drag Chain Model & Specs</th>
                      <th>Links & Brackets</th>
                      <th>Quality Check (Meters)</th>
                      <th>Proof Media</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {assembleOrders.map(order => {
                      const item = order.items?.[0] || {};
                      const cfg = item.dragChainConfig || {};
                      return (
                        <tr key={order._id}>
                          <td>
                            <div style={{ fontWeight: 700, color: 'var(--primary)' }}>
                              {order.salesOrderRef || order.dispatchNo || 'SO-PENDING'}
                            </div>
                            <div style={{ fontWeight: 600, color: 'var(--text-primary)' }}>{order.customerName}</div>
                            {order.customerContact && (
                              <div style={{ fontSize: '0.74rem', color: 'var(--text-dim)' }}>📞 {order.customerContact}</div>
                            )}
                            <OrderReleaseInfo job={order} />
                          </td>

                          <td>
                            <OrderStepBadge record={order} />
                            {order.assemblyNotes && (
                              <div style={{ fontSize: '0.72rem', color: '#f59e0b', marginTop: '4px' }}>
                                📝 {order.assemblyNotes}
                              </div>
                            )}
                          </td>

                          <td>
                            <div style={{ fontWeight: 700, color: 'var(--text-primary)' }}>
                              {cfg.modelNumber || item.productName || 'Drag Chain Item'}
                            </div>
                            <div style={{ fontSize: '0.76rem', color: 'var(--text-dim)' }}>
                              {[cfg.width && `W: ${cfg.width}`, cfg.radius && `R: ${cfg.radius}`, cfg.pitch && `Pitch: ${cfg.pitch}`].filter(Boolean).join(' | ') || '--'}
                            </div>
                            <div style={{ fontSize: '0.78rem', fontWeight: 600, color: 'var(--primary)' }}>
                              Qty: {item.quantity || 1} {item.unit || 'm'}
                            </div>
                          </td>

                          <td>
                            <div style={{ fontSize: '0.82rem', fontWeight: 700, color: 'var(--text-primary)' }}>
                              🔗 {order.linksAssembled || cfg.numberOfLinks || '--'} Links
                            </div>
                            <div style={{ fontSize: '0.74rem', color: 'var(--text-dim)' }}>
                              Brackets: {cfg.mountingBrackets || order.bracketsFitted || 'Standard'}
                            </div>
                          </td>

                          <td>
                            <QcStatusBadge order={order} />
                            {order.qcCheckedByName && (
                              <div style={{ fontSize: '0.68rem', color: 'var(--text-dim)', marginTop: '3px' }}>
                                by {order.qcCheckedByName}
                              </div>
                            )}
                          </td>

                          <td>
                            {order.assemblyMediaUrl ? (
                              <a
                                href={getMediaFullUrl(order.assemblyMediaUrl)}
                                target="_blank"
                                rel="noreferrer"
                                style={{
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  gap: '4px',
                                  fontSize: '0.76rem',
                                  color: '#10b981',
                                  fontWeight: 600,
                                  textDecoration: 'none',
                                  background: 'rgba(16,185,129,0.1)',
                                  padding: '4px 8px',
                                  borderRadius: '6px'
                                }}
                              >
                                <HiOutlinePhotograph /> View Proof
                              </a>
                            ) : (
                              <span style={{ fontSize: '0.74rem', color: 'var(--text-dim)' }}>⚠️ No photo proof</span>
                            )}
                          </td>

                          <td>
                            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                              <button
                                className="btn btn-primary btn-sm"
                                onClick={() => handleOpenProcessModal(order, 1)}
                                style={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: '4px' }}
                              >
                                🔧 Process Assembly
                              </button>

                              {canRunQc && (
                                <QcActionButton order={order} onClick={handleOpenQcModal} />
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* TAB 2: BOX PACKING QUEUE (STEP 8) */}
        {activeTab === 'packing' && (
          <div className="azure-card" style={{ padding: '24px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              <div>
                <h3 style={{ margin: 0, fontSize: '1.1rem', color: 'var(--text-primary)', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  📦 Step 8: Box Packaging & Weight Verification
                  <span className="azure-badge info">{packingOrders.length} Ready for Packing</span>
                </h3>
                <p style={{ margin: '4px 0 0', color: 'var(--text-dim)', fontSize: '0.8rem' }}>
                  Pack assembled chains into cartons, record box counts, net/gross weight, take packaging proof, and prepare for Dispatch.
                </p>
              </div>
            </div>

            {loading ? (
              <div style={{ textAlign: 'center', padding: '40px' }}><div className="spinner" /></div>
            ) : packingOrders.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '50px 20px', color: 'var(--text-dim)' }}>
                <div style={{ fontSize: '2.5rem', marginBottom: '8px' }}>📦</div>
                <div style={{ fontWeight: 700, fontSize: '1.1rem', color: 'var(--text-primary)' }}>No Orders Awaiting Packing</div>
                <div style={{ fontSize: '0.85rem' }}>As soon as assembly is finished, orders will appear here automatically.</div>
              </div>
            ) : (
              <div className="azure-table-container">
                <table className="azure-table">
                  <thead>
                    <tr>
                      <th>SO Ref & Customer</th>
                      <th>Step Status</th>
                      <th>Carton & Weight Details</th>
                      <th>Packing Proof Photo</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {packingOrders.map(order => (
                      <tr key={order._id}>
                        <td>
                          <div style={{ fontWeight: 700, color: 'var(--primary)' }}>{order.salesOrderRef || order.dispatchNo}</div>
                          <div style={{ fontWeight: 600, color: 'var(--text-primary)' }}>{order.customerName}</div>
                          <div style={{ fontSize: '0.74rem', color: 'var(--text-dim)' }}>📍 {order.customerAddress || 'Customer Address'}</div>
                        </td>

                        <td>
                          <OrderStepBadge record={order} />
                          <div style={{ fontSize: '0.74rem', color: '#6366f1', marginTop: '4px', fontWeight: 600 }}>
                            ✅ Assembly Complete
                          </div>
                        </td>

                        <td>
                          <div style={{ fontWeight: 700, color: 'var(--text-primary)' }}>
                            📦 {order.totalBoxes || 1} Box(es)
                          </div>
                          <div style={{ fontSize: '0.76rem', color: 'var(--text-dim)' }}>
                            Weight: {order.totalWeight ? `${order.totalWeight} kg` : 'Not recorded'}
                          </div>
                        </td>

                        <td>
                          {order.packingMediaUrl ? (
                            <a
                              href={getMediaFullUrl(order.packingMediaUrl)}
                              target="_blank"
                              rel="noreferrer"
                              style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '4px',
                                fontSize: '0.76rem',
                                color: '#10b981',
                                fontWeight: 600,
                                textDecoration: 'none',
                                background: 'rgba(16,185,129,0.1)',
                                padding: '4px 8px',
                                borderRadius: '6px'
                              }}
                            >
                              <HiOutlinePhotograph /> View Carton Proof
                            </a>
                          ) : (
                            <span style={{ fontSize: '0.74rem', color: 'var(--text-dim)' }}>⚠️ No box photo</span>
                          )}
                        </td>

                        <td>
                          <button
                            className="btn btn-primary btn-sm"
                            onClick={() => handleOpenProcessModal(order, 2)}
                            style={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: '4px' }}
                          >
                            📦 Update Box Details
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* TAB 3: DISPATCHED ORDERS (STEP 9) */}
        {activeTab === 'dispatched' && (
          <div className="azure-card" style={{ padding: '24px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              <div>
                <h3 style={{ margin: 0, fontSize: '1.1rem', color: 'var(--text-primary)', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  🚚 Step 9: Dispatched & Logistics Completed Archive
                  <span className="azure-badge success">{dispatchedOrders.length} Dispatched</span>
                </h3>
              </div>
            </div>

            {loading ? (
              <div style={{ textAlign: 'center', padding: '40px' }}><div className="spinner" /></div>
            ) : dispatchedOrders.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '50px 20px', color: 'var(--text-dim)' }}>
                <div style={{ fontSize: '2.5rem', marginBottom: '8px' }}>🚚</div>
                <div style={{ fontWeight: 700, fontSize: '1.1rem', color: 'var(--text-primary)' }}>No Dispatched Orders Found</div>
              </div>
            ) : (
              <div className="azure-table-container">
                <table className="azure-table">
                  <thead>
                    <tr>
                      <th>SO Ref & Customer</th>
                      <th>Step Status</th>
                      <th>Courier & LR Info</th>
                      <th>Dispatched On</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {dispatchedOrders.map(order => (
                      <tr key={order._id}>
                        <td>
                          <div style={{ fontWeight: 700, color: 'var(--primary)' }}>{order.salesOrderRef || order.dispatchNo}</div>
                          <div style={{ fontWeight: 600, color: 'var(--text-primary)' }}>{order.customerName}</div>
                        </td>
                        <td>
                          <OrderStepBadge record={order} />
                        </td>
                        <td>
                          <div style={{ fontWeight: 700, color: 'var(--text-primary)' }}>
                            🚚 {order.courier || 'Logistics Transport'}
                          </div>
                          <div style={{ fontSize: '0.76rem', color: 'var(--text-dim)' }}>
                            LR/Tracking: <strong>{order.lrNumber || order.trackingNumber || '--'}</strong>
                          </div>
                        </td>
                        <td style={{ fontSize: '0.8rem', color: 'var(--text-dim)' }}>
                          {order.dispatchedAt ? new Date(order.dispatchedAt).toLocaleString() : '--'}
                        </td>
                        <td>
                          <button
                            className="btn btn-secondary btn-sm"
                            onClick={() => handleOpenProcessModal(order, 4)}
                          >
                            View Details
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* TAB 4: FULL 9-STEP PIPELINE TRACKER */}
        {activeTab === 'pipeline_overview' && (
          <div className="azure-card" style={{ padding: '24px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', flexWrap: 'wrap', gap: '12px' }}>
              <div>
                <h3 style={{ margin: 0, fontSize: '1.1rem', color: 'var(--text-primary)', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  🔄 End-to-End 9-Step Order Funnel Overview
                </h3>
                <p style={{ margin: '4px 0 0', color: 'var(--text-dim)', fontSize: '0.8rem' }}>
                  Lead (1) ➔ Quotation (2) ➔ PO (3) ➔ PI (4) ➔ Invoice (5) ➔ Payment (6) ➔ Assembly (7) ➔ Packing (8) ➔ Dispatch (9)
                </p>
              </div>

              {/* ➕ ADD NEW PIPELINE ORDER BUTTON */}
              <button
                className="btn btn-primary btn-sm"
                onClick={() => handleOpenCreateModal(null)}
                style={{
                  fontWeight: 700,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  background: 'linear-gradient(135deg, #10b981, #059669)',
                  border: 'none',
                  boxShadow: '0 4px 12px rgba(16,185,129,0.3)'
                }}
              >
                <HiOutlinePlus style={{ strokeWidth: 3 }} /> + Create New Pipeline Deal / Order
              </button>
            </div>

            {/* Stage Filter Pills */}
            <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginBottom: '16px' }}>
              <button
                className={`btn btn-xs ${pipelineStageFilter === 'all' ? 'btn-primary' : 'btn-secondary'}`}
                onClick={() => setPipelineStageFilter('all')}
                style={{ borderRadius: '20px', padding: '4px 12px', fontSize: '0.74rem' }}
              >
                All Stages ({pipelineOrders.length})
              </button>
              {WORKFLOW_9_STEPS.map(s => (
                <button
                  key={s.key}
                  className={`btn btn-xs ${pipelineStageFilter === s.key ? 'btn-primary' : 'btn-secondary'}`}
                  onClick={() => setPipelineStageFilter(s.key)}
                  style={{ borderRadius: '20px', padding: '4px 12px', fontSize: '0.74rem' }}
                >
                  {s.icon} {s.label}
                </button>
              ))}
            </div>

            <div className="azure-table-container">
              <table className="azure-table">
                <thead>
                  <tr>
                    <th>Customer & Reference</th>
                    <th>Current 9-Step Stage</th>
                    <th>Item Specifications</th>
                    <th>Financials & Terms</th>
                    <th>Quick Stage Advance</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredPipelineOrders.length === 0 ? (
                    <tr>
                      <td colSpan="6" style={{ textAlign: 'center', padding: '40px', color: 'var(--text-dim)' }}>
                        No orders match this filter. Click <strong>"+ Create New Pipeline Deal / Order"</strong> to add one!
                      </td>
                    </tr>
                  ) : (
                    filteredPipelineOrders.map(order => {
                      const step = determineOrderStep(order);
                      const cfg = order.items?.[0]?.dragChainConfig || order.dragChainConfig || {};
                      return (
                        <tr key={order._id}>
                          <td>
                            <div style={{ fontWeight: 700, color: 'var(--text-primary)' }}>{order.customerName}</div>
                            <div style={{ fontSize: '0.78rem', color: 'var(--primary)', fontWeight: 600 }}>
                              {order.salesOrderNumber || order.quotationNumber || order.customerPoNumber || order.leadNumber || 'REF-N/A'}
                            </div>
                            {order.customerContact && (
                              <div style={{ fontSize: '0.72rem', color: 'var(--text-dim)' }}>📞 {order.customerContact}</div>
                            )}
                          </td>

                          <td>
                            <OrderStepBadge record={order} />
                          </td>

                          <td>
                            <div style={{ fontWeight: 700, color: 'var(--text-primary)', fontSize: '0.84rem' }}>
                              {cfg.modelNumber || cfg.series || order.modelNumber || 'Standard Drag Chain'}
                            </div>
                            <div style={{ fontSize: '0.74rem', color: 'var(--text-dim)' }}>
                              {[cfg.width && `W: ${cfg.width}`, cfg.radius && `R: ${cfg.radius}`, cfg.numberOfLinks && `${cfg.numberOfLinks} links`].filter(Boolean).join(' | ')}
                            </div>
                          </td>

                          <td>
                            <div style={{ fontWeight: 700, color: 'var(--text-primary)', fontSize: '0.9rem' }}>
                              ₹{(order.grandTotal || order.totalAmount || 0).toLocaleString('en-IN')}
                            </div>
                            <div style={{ fontSize: '0.72rem', color: 'var(--text-dim)' }}>
                              Approval: <strong style={{ color: order.approvalStatus === 'approved' ? '#10b981' : '#f59e0b' }}>{order.approvalStatus || 'pending'}</strong>
                            </div>
                          </td>

                          {/* 1-Click Advance Stage Shortcuts */}
                          <td>
                            <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                              {step === 1 && (
                                <button
                                  className="btn btn-secondary btn-xs"
                                  onClick={() => handleAdvancePipelineStage(order, 'quotation')}
                                  title="Advance to Quotation"
                                  style={{ color: '#3b82f6', borderColor: '#3b82f6' }}
                                >
                                  📜 ➔ Quote
                                </button>
                              )}
                              {step <= 2 && (
                                <button
                                  className="btn btn-secondary btn-xs"
                                  onClick={() => handleAdvancePipelineStage(order, 'customer_po')}
                                  title="Advance to PO"
                                  style={{ color: '#8b5cf6', borderColor: '#8b5cf6' }}
                                >
                                  📑 ➔ PO
                                </button>
                              )}
                              {step <= 3 && (
                                <button
                                  className="btn btn-secondary btn-xs"
                                  onClick={() => handleAdvancePipelineStage(order, 'pi')}
                                  title="Advance to PI"
                                  style={{ color: '#d97706', borderColor: '#d97706' }}
                                >
                                  🧾 ➔ PI
                                </button>
                              )}
                              {step <= 4 && (
                                <button
                                  className="btn btn-secondary btn-xs"
                                  onClick={() => handleAdvancePipelineStage(order, 'sales_order')}
                                  title="Advance to Sales Order / Invoice"
                                  style={{ color: '#0ea5e9', borderColor: '#0ea5e9', fontWeight: 700 }}
                                >
                                  🏷️ ➔ Order
                                </button>
                              )}
                              {step >= 5 && (
                                <button
                                  className="btn btn-primary btn-xs"
                                  onClick={() => handleOpenProcessModal(order, step >= 8 ? 2 : 1)}
                                  style={{ fontWeight: 700 }}
                                >
                                  🔧 Process Job
                                </button>
                              )}
                            </div>
                          </td>

                          {/* Action Controls */}
                          <td>
                            <div style={{ display: 'flex', gap: '6px' }}>
                              <button
                                className="btn btn-secondary btn-xs"
                                onClick={() => handleOpenCreateModal(order)}
                                title="Edit Order"
                              >
                                <HiOutlinePencil />
                              </button>
                              <button
                                className="btn btn-danger btn-xs"
                                onClick={() => handleDeletePipelineOrder(order._id)}
                                title="Delete Order"
                              >
                                <HiOutlineTrash />
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* ========================================================================= */}
        {/* ➕ CREATE / EDIT PIPELINE & ASSEMBLY WORK ORDER MODAL                     */}
        {/* ========================================================================= */}
        {showCreateModal && (
          <div className="modal-overlay" style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.75)',
            backdropFilter: 'blur(6px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: '20px'
          }}>
            <div className="azure-card" style={{
              width: '100%',
              maxWidth: '850px',
              maxHeight: '92vh',
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
              background: 'var(--bg-secondary)',
              border: '1px solid var(--border)',
              borderRadius: '16px',
              boxShadow: '0 20px 60px rgba(0,0,0,0.5)'
            }}>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '20px 28px',
                borderBottom: '1px solid var(--border)',
                flexShrink: 0
              }}>
                <div>
                  <h3 style={{ margin: 0, fontSize: '1.2rem', color: 'var(--text-primary)', fontWeight: 800 }}>
                    {editingPipelineId ? '✏️ Edit Pipeline & Assembly Work Order' : '🚀 Create New Assembly Work Order'}
                  </h3>
                  <div style={{ fontSize: '0.78rem', color: 'var(--text-dim)', marginTop: '2px' }}>
                    Standardized assembly line work order synced with Sales Pipeline and Store Inventory.
                  </div>
                </div>

                <button
                  type="button"
                  onClick={() => setShowCreateModal(false)}
                  style={{ background: 'none', border: 'none', color: 'var(--text-dim)', fontSize: '1.4rem', cursor: 'pointer' }}
                >
                  <HiOutlineX />
                </button>
              </div>

              <form onSubmit={handleSavePipelineOrder}>
                {/* 1. Customer Information */}
                <h4 style={{ margin: '0 0 12px', color: 'var(--primary)', fontSize: '0.92rem', fontWeight: 700 }}>
                  👤 1. Customer Details
                </h4>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px', marginBottom: '16px' }}>
                  <div>
                    <label className="form-label">Customer / Company Name *</label>
                    <input
                      type="text"
                      className="form-input"
                      placeholder="e.g. Tata Motors / BlueStar Ltd"
                      value={pipelineForm.customerName}
                      onChange={e => setPipelineForm({ ...pipelineForm, customerName: e.target.value })}
                      required
                    />
                  </div>

                  <div>
                    <label className="form-label">Phone / WhatsApp Number</label>
                    <input
                      type="text"
                      className="form-input"
                      placeholder="e.g. 9876543210"
                      value={pipelineForm.customerContact}
                      onChange={e => setPipelineForm({ ...pipelineForm, customerContact: e.target.value })}
                    />
                  </div>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px', marginBottom: '20px' }}>
                  <div>
                    <label className="form-label">Email Address</label>
                    <input
                      type="email"
                      className="form-input"
                      placeholder="e.g. purchase@tatamotors.com"
                      value={pipelineForm.customerEmail}
                      onChange={e => setPipelineForm({ ...pipelineForm, customerEmail: e.target.value })}
                    />
                  </div>

                  <div>
                    <label className="form-label">GST Number (Optional)</label>
                    <input
                      type="text"
                      className="form-input"
                      placeholder="e.g. 07AAAAA0000A1Z5"
                      value={pipelineForm.customerGST}
                      onChange={e => setPipelineForm({ ...pipelineForm, customerGST: e.target.value })}
                    />
                  </div>
                </div>

                {/* 2. Drag Chain Specifications */}
                <h4 style={{ margin: '0 0 12px', color: '#f59e0b', fontSize: '0.92rem', fontWeight: 700 }}>
                  ⚙️ 2. Drag Chain Specifications
                </h4>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '14px', marginBottom: '16px' }}>
                  <div>
                    <label className="form-label">Model Series *</label>
                    <input
                      type="text"
                      className="form-input"
                      placeholder="e.g. C.36 / S24.25.1"
                      value={pipelineForm.dragChainConfig.modelNumber}
                      onChange={e => {
                        const val = e.target.value;
                        const match = productTemplates.find(t => t.modelNumber === val);
                        setPipelineForm({
                          ...pipelineForm,
                          dragChainConfig: {
                            ...pipelineForm.dragChainConfig,
                            modelNumber: val,
                            series: val,
                            pitch: match?.pitch || pipelineForm.dragChainConfig.pitch,
                            ratePerMeter: match?.ratePerMeter || match?.unitPrice || pipelineForm.dragChainConfig.ratePerMeter
                          }
                        });
                      }}
                      required
                    />
                  </div>

                  <div>
                    <label className="form-label">Inner Width (mm)</label>
                    <input
                      type="text"
                      className="form-input"
                      placeholder="e.g. 50"
                      value={pipelineForm.dragChainConfig.width}
                      onChange={e => setPipelineForm({
                        ...pipelineForm,
                        dragChainConfig: { ...pipelineForm.dragChainConfig, width: e.target.value }
                      })}
                    />
                  </div>

                  <div>
                    <label className="form-label">Bending Radius (R)</label>
                    <input
                      type="text"
                      className="form-input"
                      placeholder="e.g. 125"
                      value={pipelineForm.dragChainConfig.radius}
                      onChange={e => setPipelineForm({
                        ...pipelineForm,
                        dragChainConfig: { ...pipelineForm.dragChainConfig, radius: e.target.value }
                      })}
                    />
                  </div>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '14px', marginBottom: '20px' }}>
                  <div>
                    <label className="form-label">Pitch (mm)</label>
                    <input
                      type="number"
                      className="form-input"
                      placeholder="e.g. 46"
                      value={pipelineForm.dragChainConfig.pitch}
                      onChange={e => setPipelineForm({
                        ...pipelineForm,
                        dragChainConfig: { ...pipelineForm.dragChainConfig, pitch: parseFloat(e.target.value) || 46 }
                      })}
                    />
                  </div>

                  <div>
                    <label className="form-label">Links Count</label>
                    <input
                      type="number"
                      className="form-input"
                      placeholder="e.g. 22"
                      value={pipelineForm.dragChainConfig.numberOfLinks}
                      onChange={e => setPipelineForm({
                        ...pipelineForm,
                        dragChainConfig: { ...pipelineForm.dragChainConfig, numberOfLinks: parseInt(e.target.value, 10) || 22 }
                      })}
                    />
                  </div>

                  <div>
                    <label className="form-label">Mounting Brackets</label>
                    <select
                      className="form-input"
                      value={pipelineForm.dragChainConfig.mountingBrackets}
                      onChange={e => setPipelineForm({
                        ...pipelineForm,
                        dragChainConfig: { ...pipelineForm.dragChainConfig, mountingBrackets: e.target.value }
                      })}
                    >
                      <option value="Standard">Standard Pair</option>
                      <option value="Inner Pair">Inner Pair</option>
                      <option value="Outer Pair">Outer Pair</option>
                      <option value="No Brackets">No Brackets</option>
                    </select>
                  </div>
                </div>

                {/* 3. Stage Selection & Pricing */}
                <h4 style={{ margin: '0 0 12px', color: '#10b981', fontSize: '0.92rem', fontWeight: 700 }}>
                  🏷️ 3. Stage & Commercial Details
                </h4>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px', marginBottom: '20px' }}>
                  <div>
                    <label className="form-label">Initial 9-Step Stage *</label>
                    <select
                      className="form-input"
                      value={pipelineForm.stage}
                      onChange={e => setPipelineForm({ ...pipelineForm, stage: e.target.value })}
                    >
                      <option value="lead">Step 1: 🎯 Lead Inquiry</option>
                      <option value="quotation">Step 2: 📜 Quotation Sent</option>
                      <option value="customer_po">Step 3: 📑 Customer PO Received</option>
                      <option value="pi">Step 4: 🧾 Proforma Invoice (PI)</option>
                      <option value="sales_order">Step 5/7: 🏷️ Sales Order (Direct to Assembly Queue)</option>
                    </select>
                  </div>

                  <div>
                    <label className="form-label">Total Amount (₹) *</label>
                    <input
                      type="number"
                      className="form-input"
                      placeholder="e.g. 15000"
                      value={pipelineForm.grandTotal}
                      onChange={e => setPipelineForm({ ...pipelineForm, grandTotal: parseFloat(e.target.value) || 0 })}
                      required
                    />
                  </div>
                </div>

                <div style={{
                  display: 'flex',
                  justifyContent: 'flex-end',
                  gap: '10px',
                  borderTop: '1px solid var(--border)',
                  paddingTop: '16px'
                }}>
                  <button
                    type="button"
                    className="btn btn-secondary btn-sm"
                    onClick={() => setShowCreateModal(false)}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="btn btn-primary btn-sm"
                    disabled={creatingOrder}
                    style={{
                      fontWeight: 800,
                      background: 'linear-gradient(135deg, #10b981, #059669)',
                      border: 'none',
                      padding: '8px 20px'
                    }}
                  >
                    {creatingOrder ? 'Saving...' : editingPipelineId ? '💾 Update Order' : '🚀 Create Order & Add to Line'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* ========================================================================= */}
        {/* MULTI-STEP WORKFLOW MODAL WITH SAVE AS DRAFT ON EVERY SINGLE STEP         */}
        {/* ========================================================================= */}
        {showModal && selectedOrder && (
          <div className="modal-overlay" style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.75)',
            backdropFilter: 'blur(6px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: '20px'
          }}>
            <div className="azure-card" style={{
              width: '100%',
              maxWidth: '850px',
              maxHeight: '90vh',
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
              background: 'var(--bg-secondary)',
              border: '1px solid var(--border)',
              borderRadius: '16px',
              boxShadow: '0 20px 60px rgba(0,0,0,0.5)'
            }}>
              {/* Modal Header */}
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '20px 28px',
                borderBottom: '1px solid var(--border)',
                flexShrink: 0
              }}>
                <div>
                  <h3 style={{ margin: 0, fontSize: '1.2rem', color: 'var(--text-primary)', fontWeight: 800 }}>
                    🔧 Assembly & Dispatch Work Order
                  </h3>
                  <div style={{ fontSize: '0.8rem', color: 'var(--primary)', fontWeight: 600, marginTop: '2px' }}>
                    Order: {formData.salesOrderRef || selectedOrder.dispatchNo} | Customer: {formData.customerName}
                  </div>
                </div>

                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  style={{ background: 'none', border: 'none', color: 'var(--text-dim)', fontSize: '1.4rem', cursor: 'pointer' }}
                >
                  <HiOutlineX />
                </button>
              </div>

              {/* Scrollable Modal Body */}
              <div style={{ padding: '24px 28px', overflowY: 'auto', overflowX: 'hidden', flex: 1 }}>

              {/* Step Navigation Indicator */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(4, 1fr)',
                gap: '8px',
                marginBottom: '24px'
              }}>
                {[
                  { num: 1, label: 'Step 1: Assembly & QC', icon: '🔧' },
                  { num: 2, label: 'Step 2: Box Packing', icon: '📦' },
                  { num: 3, label: 'Step 3: Billing Invoice', icon: '🧾' },
                  { num: 4, label: 'Step 4: Dispatch LR', icon: '🚚' }
                ].map(s => (
                  <button
                    key={s.num}
                    type="button"
                    onClick={() => setModalStep(s.num)}
                    style={{
                      padding: '10px 8px',
                      borderRadius: '8px',
                      border: modalStep === s.num ? '2px solid var(--primary)' : '1px solid var(--border)',
                      background: modalStep === s.num ? 'rgba(59,130,246,0.15)' : 'rgba(255,255,255,0.02)',
                      color: modalStep === s.num ? 'var(--primary)' : 'var(--text-dim)',
                      fontWeight: 700,
                      fontSize: '0.78rem',
                      cursor: 'pointer',
                      textAlign: 'center'
                    }}
                  >
                    <div>{s.icon}</div>
                    <div style={{ marginTop: '2px' }}>{s.label}</div>
                  </button>
                ))}
              </div>

              {/* STEP 1: ASSEMBLY DETAILS */}
              {modalStep === 1 && (
                <div>
                  <h4 style={{ margin: '0 0 16px', color: '#f59e0b', fontSize: '1rem', fontWeight: 700 }}>
                    🔧 Step 1: Drag Chain Assembly & Media Proofs
                  </h4>

                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
                    <div>
                      <label className="form-label">Total Links Assembled</label>
                      <input
                        type="number"
                        className="form-input"
                        placeholder="e.g. 45"
                        value={formData.linksAssembled}
                        onChange={e => setFormData({ ...formData, linksAssembled: e.target.value })}
                      />
                    </div>

                    <div>
                      <label className="form-label">Mounting Brackets Fitted?</label>
                      <select
                        className="form-input"
                        value={formData.bracketsFitted}
                        onChange={e => setFormData({ ...formData, bracketsFitted: e.target.value })}
                      >
                        <option value="yes">✅ Yes, End Brackets Fitted</option>
                        <option value="no">❌ No Brackets</option>
                        <option value="loose">📦 Packed Separately Loose</option>
                      </select>
                    </div>
                  </div>

                  <div style={{ marginBottom: '16px' }}>
                    <label className="form-label">Assembly Notes / Inspection Remarks</label>
                    <textarea
                      className="form-input"
                      rows="2"
                      placeholder="e.g. Assembled 2.5m drag chain, pitch verified, bending radius smooth..."
                      value={formData.assemblyNotes}
                      onChange={e => setFormData({ ...formData, assemblyNotes: e.target.value })}
                    />
                  </div>

                  {/* Photo / Video Proof Upload */}
                  <div style={{
                    padding: '16px',
                    borderRadius: '12px',
                    background: 'rgba(255,255,255,0.02)',
                    border: '1px dashed var(--border)',
                    marginBottom: '20px'
                  }}>
                    <label className="form-label" style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <HiOutlinePhotograph /> Assembly Photo / Video Proof
                    </label>
                    <input
                      type="file"
                      accept="image/*,video/*"
                      onChange={e => handleMediaUpload(e.target.files[0], 'assemblyMediaUrl')}
                      disabled={uploadingMedia}
                    />
                    {uploadingMedia && <span style={{ fontSize: '0.78rem', color: 'var(--primary)' }}>Uploading media proof...</span>}

                    {formData.assemblyMediaUrl && (
                      <div style={{ marginTop: '10px' }}>
                        <a
                          href={getMediaFullUrl(formData.assemblyMediaUrl)}
                          target="_blank"
                          rel="noreferrer"
                          style={{ fontSize: '0.8rem', color: '#10b981', fontWeight: 600 }}
                        >
                          ✅ Uploaded Proof File (Click to View)
                        </a>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* STEP 2: PACKING DETAILS */}
              {modalStep === 2 && (
                <div>
                  <h4 style={{ margin: '0 0 16px', color: '#6366f1', fontSize: '1rem', fontWeight: 700 }}>
                    📦 Step 2: Box Packaging & Weight Verification
                  </h4>

                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
                    <div>
                      <label className="form-label">Total Carton / Box Count *</label>
                      <input
                        type="number"
                        className="form-input"
                        placeholder="e.g. 2"
                        value={formData.totalBoxes}
                        onChange={e => setFormData({ ...formData, totalBoxes: e.target.value })}
                      />
                    </div>

                    <div>
                      <label className="form-label">Gross Weight (kg)</label>
                      <input
                        type="text"
                        className="form-input"
                        placeholder="e.g. 12.5 kg"
                        value={formData.totalWeight}
                        onChange={e => setFormData({ ...formData, totalWeight: e.target.value })}
                      />
                    </div>
                  </div>

                  <div style={{ marginBottom: '16px' }}>
                    <label className="form-label">Packaging / Box Labels Note</label>
                    <textarea
                      className="form-input"
                      rows="2"
                      placeholder="e.g. Packed in 7-ply corrugated box with bubble wrap and product label..."
                      value={formData.packingNotes}
                      onChange={e => setFormData({ ...formData, packingNotes: e.target.value })}
                    />
                  </div>

                  {/* Carton Photo Upload */}
                  <div style={{
                    padding: '16px',
                    borderRadius: '12px',
                    background: 'rgba(255,255,255,0.02)',
                    border: '1px dashed var(--border)',
                    marginBottom: '20px'
                  }}>
                    <label className="form-label" style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <HiOutlinePhotograph /> Carton & Box Photo Proof
                    </label>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={e => handleMediaUpload(e.target.files[0], 'packingMediaUrl')}
                      disabled={uploadingMedia}
                    />

                    {formData.packingMediaUrl && (
                      <div style={{ marginTop: '10px' }}>
                        <a
                          href={getMediaFullUrl(formData.packingMediaUrl)}
                          target="_blank"
                          rel="noreferrer"
                          style={{ fontSize: '0.8rem', color: '#10b981', fontWeight: 600 }}
                        >
                          ✅ Uploaded Box Photo (Click to View)
                        </a>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* STEP 3: BILLING & CHALLAN */}
              {modalStep === 3 && (
                <div>
                  <h4 style={{ margin: '0 0 16px', color: '#0ea5e9', fontSize: '1rem', fontWeight: 700 }}>
                    🧾 Step 3: Tax Invoice & Delivery Challan Verification
                  </h4>

                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
                    <div>
                      <label className="form-label">Tax Invoice / Challan Number</label>
                      <input
                        type="text"
                        className="form-input"
                        placeholder="e.g. INV-2026-0042"
                        value={formData.invoiceNo}
                        onChange={e => setFormData({ ...formData, invoiceNo: e.target.value })}
                      />
                    </div>

                    <div>
                      <label className="form-label">Upload Invoice / Challan Copy</label>
                      <input
                        type="file"
                        accept="image/*,.pdf"
                        onChange={e => handleMediaUpload(e.target.files[0], 'billingInvoiceUrl')}
                        disabled={uploadingMedia}
                      />
                    </div>
                  </div>

                  {formData.billingInvoiceUrl && (
                    <div style={{ marginTop: '10px', marginBottom: '16px' }}>
                      <a
                        href={getMediaFullUrl(formData.billingInvoiceUrl)}
                        target="_blank"
                        rel="noreferrer"
                        style={{ fontSize: '0.8rem', color: '#10b981', fontWeight: 600 }}
                      >
                        ✅ View Uploaded Invoice / DC Document
                      </a>
                    </div>
                  )}
                </div>
              )}

              {/* STEP 4: DISPATCH & LOGISTICS */}
              {modalStep === 4 && (
                <div>
                  <h4 style={{ margin: '0 0 16px', color: '#22c55e', fontSize: '1rem', fontWeight: 700 }}>
                    🚚 Step 4: Transporter / Courier & Outward Dispatch
                  </h4>

                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
                    <div>
                      <label className="form-label">Courier / Transporter Name *</label>
                      <input
                        type="text"
                        className="form-input"
                        placeholder="e.g. VRL Logistics / Blue Dart / Delhivery"
                        value={formData.courier}
                        onChange={e => setFormData({ ...formData, courier: e.target.value })}
                      />
                    </div>

                    <div>
                      <label className="form-label">LR Number / Docket Number *</label>
                      <input
                        type="text"
                        className="form-input"
                        placeholder="e.g. LR-984210"
                        value={formData.lrNumber}
                        onChange={e => setFormData({ ...formData, lrNumber: e.target.value })}
                      />
                    </div>
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
                    <div>
                      <label className="form-label">Live Tracking URL / Docket Link</label>
                      <input
                        type="text"
                        className="form-input"
                        placeholder="e.g. https://track.vrl.com/12345"
                        value={formData.trackingNumber}
                        onChange={e => setFormData({ ...formData, trackingNumber: e.target.value })}
                      />
                    </div>

                    <div>
                      <label className="form-label">Delivery / Shipping Address</label>
                      <input
                        type="text"
                        className="form-input"
                        value={formData.customerAddress}
                        onChange={e => setFormData({ ...formData, customerAddress: e.target.value })}
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Modal Action Bar with Draft Save and Stage Advancement */}
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                borderTop: '1px solid var(--border)',
                paddingTop: '18px',
                marginTop: '20px',
                flexWrap: 'wrap',
                gap: '12px'
              }}>
                <button
                  type="button"
                  className="btn btn-secondary btn-sm"
                  onClick={handleSaveDraft}
                  disabled={savingDraft}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    borderColor: '#f59e0b',
                    color: '#f59e0b',
                    fontWeight: 700
                  }}
                >
                  <HiOutlineSave /> {savingDraft ? 'Saving Draft...' : '💾 Save as Draft'}
                </button>

                <div style={{ display: 'flex', gap: '10px' }}>
                  {modalStep > 1 && (
                    <button
                      type="button"
                      className="btn btn-secondary btn-sm"
                      onClick={() => setModalStep(modalStep - 1)}
                    >
                      <HiOutlineArrowLeft /> Back
                    </button>
                  )}

                  {modalStep === 1 && (
                    <button
                      type="button"
                      className="btn btn-primary btn-sm"
                      onClick={() => {
                        handleAdvanceStep('packing');
                        setModalStep(2);
                      }}
                      style={{ fontWeight: 700 }}
                    >
                      Complete Assembly & Move to Packing ➔
                    </button>
                  )}

                  {modalStep === 2 && (
                    <button
                      type="button"
                      className="btn btn-primary btn-sm"
                      onClick={() => setModalStep(3)}
                      style={{ fontWeight: 700 }}
                    >
                      Next: Billing & Challan ➔
                    </button>
                  )}

                  {modalStep === 3 && (
                    <button
                      type="button"
                      className="btn btn-primary btn-sm"
                      onClick={() => setModalStep(4)}
                      style={{ fontWeight: 700 }}
                    >
                      Next: Dispatch Details ➔
                    </button>
                  )}

                  {modalStep === 4 && (
                    <button
                      type="button"
                      className="btn btn-success btn-sm"
                      onClick={() => handleAdvanceStep('dispatched')}
                      disabled={savingDraft}
                      style={{
                        background: '#22c55e',
                        color: '#fff',
                        fontWeight: 800,
                        border: 'none',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px'
                      }}
                    >
                      <HiOutlineTruck /> 🚚 Final Save & Mark Dispatched
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

        {/* Pre-Dispatch Quality Check (shared component, meters only) */}
        {showQcModal && qcOrder && (
          <DispatchQcModal
            order={qcOrder}
            onClose={handleCloseQcModal}
            onSaved={fetchData}
          />
        )}

      </div>
    </DashboardLayout>
  );
}
